import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { enterpriseApi, pipelineApi } from "@/api/endpoints";
import type { AgencyUserSummary, Lead } from "@/api/types";
import { useAuth } from "@/auth/AuthContext";
import { canAccess } from "@/auth/permissions";
import { Modal } from "@/components/Modal";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { LEAD_STAGES, PRODUCT_LINES, US_STATES } from "@/constants/crm";
import { formatCurrency } from "@/utils/format";

type ViewMode = "list" | "board";

const FUNNEL_STAGES: Array<{ key: string; label: string; stages: string[] }> = [
  { key: "prospect", label: "Prospect", stages: ["new", "contacted", "discovery_call"] },
  { key: "qualify", label: "Qualify", stages: ["needs_info"] },
  { key: "quote", label: "Quote", stages: ["quoted", "proposal_sent"] },
  { key: "close", label: "Close", stages: ["negotiation", "bound"] },
];

const STAGE_LABELS: Record<string, string> = {
  new: "New",
  contacted: "Contacted",
  discovery_call: "Discovery",
  needs_info: "Needs analysis",
  quoted: "Quoting",
  proposal_sent: "Proposal sent",
  negotiation: "Follow up",
  bound: "Bound",
  renewal_pipeline: "Renewal",
};

export function PipelinePage() {
  const { user } = useAuth();
  const canEdit = canAccess(user, "crm.edit");
  const [lane, setLane] = useState<"sales" | "renewal">("sales");
  const [view, setView] = useState<ViewMode>("list");
  const [query, setQuery] = useState("");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  function reloadLeads() {
    return pipelineApi
      .leads(lane)
      .then((data) => setLeads(data.items))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load pipeline"));
  }

  useEffect(() => {
    reloadLeads();
  }, [lane]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((lead) => {
      const haystack = [lead.contact_name, lead.company_name, lead.product_line, lead.contact_email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [leads, query]);

  const totalPremium = filtered.reduce((sum, lead) => sum + (lead.estimated_premium_cents ?? 0), 0);
  const hotLeads = filtered.filter((lead) => (lead.close_probability ?? 0) >= 60).length;

  return (
    <>
      <PageHeader
        title="Pipeline"
        subtitle="Streamlined sales flow — scan leads in a list or grouped board."
      />

      <div className="pipeline-toolbar">
        <div className="pipeline-toolbar-actions">
          <button
            type="button"
            className={`btn ${lane === "sales" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setLane("sales")}
          >
            Sales
          </button>
          <button
            type="button"
            className={`btn ${lane === "renewal" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setLane("renewal")}
          >
            Renewal
          </button>
          <button
            type="button"
            className={`btn ${view === "list" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setView("list")}
          >
            List
          </button>
          <button
            type="button"
            className={`btn ${view === "board" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setView("board")}
          >
            Board
          </button>
        </div>
        <div className="pipeline-toolbar-actions">
          <input
            type="search"
            placeholder="Search leads…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ minWidth: "12rem" }}
          />
          {canEdit && lane === "sales" ? (
            <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
              New lead
            </button>
          ) : null}
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <section className="stat-grid">
        <StatCard label="Leads" value={filtered.length} />
        <StatCard label="Pipeline value" value={formatCurrency(totalPremium)} />
        <StatCard label="Hot (60%+ close)" value={hotLeads} />
      </section>

      {view === "list" ? (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {filtered.length === 0 ? (
            <p className="empty-state" style={{ padding: "1rem" }}>
              No leads in this lane.
            </p>
          ) : (
            <table className="data-table pipeline-list-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Line</th>
                  <th>Premium</th>
                  <th>Stage</th>
                  <th>Close %</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((lead) => (
                  <LeadRow key={lead.id} lead={lead} lane={lane} onMoved={reloadLeads} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : lane === "renewal" ? (
        <div className="pipeline-funnel">
          <FunnelColumn
            label="Renewal pipeline"
            leads={filtered}
            onMoved={reloadLeads}
            stageKeys={["renewal_pipeline"]}
          />
        </div>
      ) : (
        <div className="pipeline-funnel">
          {FUNNEL_STAGES.map((group) => (
            <FunnelColumn
              key={group.key}
              label={group.label}
              leads={filtered.filter((lead) => group.stages.includes(lead.stage))}
              stageKeys={group.stages}
              onMoved={reloadLeads}
            />
          ))}
        </div>
      )}

      <NewLeadModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          reloadLeads();
        }}
        currentUserId={user?.id}
      />
    </>
  );
}

function LeadRow({
  lead,
  lane,
  onMoved,
}: {
  lead: Lead;
  lane: "sales" | "renewal";
  onMoved: () => void;
}) {
  const name = lead.contact_name || lead.company_name || "Lead";
  const stages =
    lane === "renewal"
      ? [{ key: "renewal_pipeline", label: "Renewal pipeline" }]
      : FUNNEL_STAGES.flatMap((group) => group.stages).map((key) => ({
          key,
          label: STAGE_LABELS[key] ?? key,
        }));

  return (
    <tr>
      <td>
        <strong>{name}</strong>
        {lead.company_name && lead.contact_name ? (
          <div className="pipeline-card-meta">{lead.company_name}</div>
        ) : null}
      </td>
      <td>{lead.product_line}</td>
      <td>{lead.estimated_premium_cents ? formatCurrency(lead.estimated_premium_cents) : "—"}</td>
      <td>
        <select
          value={lead.stage}
          onChange={async (e) => {
            await pipelineApi.updateLead(lead.id, { stage: e.target.value });
            onMoved();
          }}
          style={{ minWidth: "8rem" }}
        >
          {stages.map((stage) => (
            <option key={stage.key} value={stage.key}>
              {stage.label}
            </option>
          ))}
        </select>
      </td>
      <td>{lead.close_probability != null ? `${lead.close_probability}%` : "—"}</td>
      <td>
        <Link to={`/rate-shop?leadId=${lead.id}`} className="btn btn-secondary">
          Quote
        </Link>
      </td>
    </tr>
  );
}

function FunnelColumn({
  label,
  leads,
  stageKeys,
  onMoved,
}: {
  label: string;
  leads: Lead[];
  stageKeys: string[];
  onMoved: () => void;
}) {
  return (
    <div className="pipeline-funnel-column card">
      <h4>
        <span>{label}</span>
        <span className="kanban-count">{leads.length}</span>
      </h4>
      {leads.length === 0 ? (
        <p className="empty-state" style={{ padding: "0.25rem 0" }}>
          Empty
        </p>
      ) : (
        leads.map((lead) => (
          <CompactLeadCard
            key={lead.id}
            lead={lead}
            stageOptions={stageKeys.map((key) => ({ key, label: STAGE_LABELS[key] ?? key }))}
            onMoved={onMoved}
          />
        ))
      )}
    </div>
  );
}

function CompactLeadCard({
  lead,
  stageOptions,
  onMoved,
}: {
  lead: Lead;
  stageOptions: Array<{ key: string; label: string }>;
  onMoved: () => void;
}) {
  const name = lead.contact_name || lead.company_name || "Lead";
  const currentIndex = stageOptions.findIndex((s) => s.key === lead.stage);
  const nextStage = currentIndex >= 0 && currentIndex < stageOptions.length - 1 ? stageOptions[currentIndex + 1] : null;

  return (
    <div className="pipeline-card">
      <div className="pipeline-card-head">
        <div>
          <strong style={{ fontSize: "0.9rem" }}>{name}</strong>
          <div className="pipeline-card-meta">
            {lead.product_line}
            {lead.estimated_premium_cents ? ` · ${formatCurrency(lead.estimated_premium_cents)}` : ""}
          </div>
        </div>
        {lead.close_probability != null ? (
          <span className="badge">{lead.close_probability}%</span>
        ) : null}
      </div>
      <div className="pipeline-card-actions">
        {nextStage ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={async () => {
              await pipelineApi.updateLead(lead.id, { stage: nextStage.key });
              onMoved();
            }}
          >
            → {nextStage.label}
          </button>
        ) : null}
        <Link to={`/rate-shop?leadId=${lead.id}`} className="btn btn-secondary">
          Quote
        </Link>
      </div>
    </div>
  );
}

function NewLeadModal({
  open,
  onClose,
  onCreated,
  currentUserId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  currentUserId?: string;
}) {
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [productLine, setProductLine] = useState(PRODUCT_LINES[0]);
  const [premium, setPremium] = useState("");
  const [state, setState] = useState("");
  const [stage, setStage] = useState("new");
  const [assigneeId, setAssigneeId] = useState("");
  const [notes, setNotes] = useState("");
  const [producers, setProducers] = useState<AgencyUserSummary[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    enterpriseApi
      .agencyUsers({ role: "producer" })
      .then((res) => setProducers(res.items))
      .catch(() => setProducers([]));
    setAssigneeId(currentUserId ?? "");
  }, [open, currentUserId]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!contactName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const descriptionParts = [notes.trim()];
      if (state) descriptionParts.unshift(`State: ${state}`);
      await pipelineApi.createLead({
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim() || undefined,
        company_name: companyName.trim() || undefined,
        product_line: productLine,
        estimated_premium_cents: premium ? Math.round(parseFloat(premium) * 100) : undefined,
        stage,
        description: descriptionParts.filter(Boolean).join("\n") || undefined,
        assigned_to_id: assigneeId || undefined,
        pipeline_lane: "sales",
      });
      setContactName("");
      setContactEmail("");
      setCompanyName("");
      setPremium("");
      setState("");
      setStage("new");
      setNotes("");
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create lead");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} title="New lead" onClose={onClose}>
      <form onSubmit={onSubmit} style={{ display: "grid", gap: "0.75rem" }}>
        <label>
          Contact name *
          <input value={contactName} onChange={(e) => setContactName(e.target.value)} required style={{ width: "100%" }} />
        </label>
        <label>
          Email
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} style={{ width: "100%" }} />
        </label>
        <label>
          Company
          <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} style={{ width: "100%" }} />
        </label>
        <label>
          Product line *
          <select value={productLine} onChange={(e) => setProductLine(e.target.value)} style={{ width: "100%" }}>
            {PRODUCT_LINES.map((line) => (
              <option key={line} value={line}>
                {line}
              </option>
            ))}
          </select>
        </label>
        <label>
          Estimated premium (USD)
          <input type="number" min="0" step="0.01" value={premium} onChange={(e) => setPremium(e.target.value)} style={{ width: "100%" }} />
        </label>
        <label>
          State
          <select value={state} onChange={(e) => setState(e.target.value)} style={{ width: "100%" }}>
            <option value="">Select state…</option>
            {US_STATES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>
        <label>
          Initial stage
          <select value={stage} onChange={(e) => setStage(e.target.value)} style={{ width: "100%" }}>
            {LEAD_STAGES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Assign to producer
          <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} style={{ width: "100%" }}>
            <option value="">Unassigned</option>
            {producers.map((producer) => (
              <option key={producer.id} value={producer.id}>
                {producer.display_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ width: "100%" }} />
        </label>
        {error ? <div className="error-banner">{error}</div> : null}
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Creating…" : "Create lead"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
